import React from "react";
import classnames from "classnames";

import Loaders from "components/Common/Loaders";

const normalizeStatusText = (value) => String(value ?? "").trim().toLowerCase();

export const extractFadvDriverProfileMetadataRowsFromApiResponse = (apiResponse) => {
    const payload = apiResponse?.data ?? apiResponse;
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    return [];
};

const resolveDriverProfileMetadataTone = (status) => {
    const normalized = normalizeStatusText(status);
    if (!normalized) return "neutral";
    if (
        normalized.includes("missing") ||
        normalized.includes("expired") ||
        normalized.includes("fail") ||
        normalized.includes("reject")
    ) {
        return "danger";
    }
    if (
        normalized.includes("reviewed") ||
        normalized.includes("complete") ||
        normalized.includes("approved") ||
        normalized.includes("clear")
    ) {
        return "success";
    }
    if (
        normalized.includes("monitored") ||
        normalized.includes("waiver") ||
        normalized.includes("pending") ||
        normalized.includes("progress")
    ) {
        return "info";
    }
    return "neutral";
};

const resolveSectionIconClass = (section) => {
    const key = normalizeStatusText(section).replace(/[\s_-]+/g, "");
    if (key.includes("mvr")) return "mdi mdi-car-side";
    if (key.includes("road")) return "mdi mdi-road-variant";
    if (key.includes("training") || key.includes("cert")) return "mdi mdi-certificate-outline";
    if (key.includes("mec") || key.includes("medical")) return "mdi mdi-hospital-box-outline";
    if (key.includes("dhi")) return "mdi mdi-shield-check-outline";
    if (key.includes("application")) return "mdi mdi-file-document-outline";
    return "mdi mdi-clipboard-check-outline";
};

const formatMetadataDisplayValue = (value) => {
    if (value === null || value === undefined || String(value).trim() === "") {
        return "—";
    }
    return String(value).trim();
};

const DRIVER_PROFILE_METADATA_COLUMN_COUNT = 5;

const CallDetailsDriverProfileMetadataSection = ({
    candidateClientId = "",
    rows = [],
    loading = false,
    loadError = "",
    onRefresh,
}) => {
    const ccid = String(candidateClientId ?? "").trim();
    const showInitialLoader = loading && rows.length === 0;

    if (!ccid) {
        return (
            <p className="text-muted small mb-0 padding-lft">
                Candidate client ID is not available yet.
            </p>
        );
    }

    const renderTableHeader = () => (
        <thead>
            <tr>
                <th scope="col">Section</th>
                <th scope="col">Status</th>
                <th scope="col">Expires</th>
                <th scope="col">Journal notes</th>
                <th
                    scope="col"
                    className="cd-driver-profile-metadata__refresh-col"
                    aria-label="Refresh actions"
                >
                    <button
                        type="button"
                        className="cd-driver-profile-metadata__header-refresh"
                        onClick={onRefresh}
                        disabled={loading}
                        title="Refresh driver profile data"
                        aria-label="Refresh driver profile metadata"
                    >
                        <i
                            className={classnames(
                                "mdi mdi-refresh",
                                loading && "mdi-spin"
                            )}
                            aria-hidden
                        />
                    </button>
                </th>
            </tr>
        </thead>
    );

    return (
        <div className="cd-driver-profile-metadata padding-lft">
            <div className="table-responsive cd-driver-profile-metadata__table-wrap">
                <table className="table table-sm cd-driver-profile-metadata__table mb-0">
                    {renderTableHeader()}
                    <tbody>
                        {showInitialLoader ? (
                            <tr>
                                <td
                                    colSpan={DRIVER_PROFILE_METADATA_COLUMN_COUNT}
                                    className="cd-driver-profile-metadata__state-cell"
                                >
                                    <Loaders />
                                </td>
                            </tr>
                        ) : loadError ? (
                            <tr>
                                <td
                                    colSpan={DRIVER_PROFILE_METADATA_COLUMN_COUNT}
                                    className="cd-driver-profile-metadata__state-cell text-danger small"
                                >
                                    {loadError}
                                </td>
                            </tr>
                        ) : rows.length === 0 ? (
                            <tr>
                                <td
                                    colSpan={DRIVER_PROFILE_METADATA_COLUMN_COUNT}
                                    className="cd-driver-profile-metadata__state-cell text-muted small"
                                >
                                    No driver profile metadata for this candidate.
                                </td>
                            </tr>
                        ) : (
                            rows.map((row, index) => {
                                const section = formatMetadataDisplayValue(row?.section);
                                const status = formatMetadataDisplayValue(row?.status);
                                const expiresOn = formatMetadataDisplayValue(row?.expiresOn);
                                const journalNotes = formatMetadataDisplayValue(row?.journalNotes);
                                const tone = resolveDriverProfileMetadataTone(row?.status);
                                const rowKey = `${section}-${index}`;
                                const hasJournalNotes = journalNotes !== "—";
                                const hasExpiresOn = expiresOn !== "—";

                                return (
                                    <tr
                                        key={rowKey}
                                        className="cd-driver-profile-metadata__row"
                                    >
                                        <td className="cd-driver-profile-metadata__section-cell">
                                            <div className="cd-driver-profile-metadata__section-inner">
                                                <span
                                                    className={classnames(
                                                        "cd-driver-profile-metadata__icon",
                                                        `cd-driver-profile-metadata__icon--${tone}`
                                                    )}
                                                    aria-hidden
                                                >
                                                    <i
                                                        className={resolveSectionIconClass(section)}
                                                    />
                                                </span>
                                                <span className="cd-driver-profile-metadata__section">
                                                    {section}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="cd-driver-profile-metadata__status-cell">
                                            {status}
                                        </td>
                                        <td
                                            className={classnames(
                                                "cd-driver-profile-metadata__expires",
                                                !hasExpiresOn &&
                                                    "cd-driver-profile-metadata__expires--empty"
                                            )}
                                        >
                                            {expiresOn}
                                        </td>
                                        <td
                                            className={classnames(
                                                "cd-driver-profile-metadata__notes",
                                                !hasJournalNotes &&
                                                    "cd-driver-profile-metadata__notes--empty"
                                            )}
                                        >
                                            {journalNotes}
                                        </td>
                                        <td
                                            className="cd-driver-profile-metadata__refresh-spacer"
                                            aria-hidden
                                        />
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default CallDetailsDriverProfileMetadataSection;
